import { useState, useEffect, useRef } from "react";
import { api } from "./api";

const BASE = "https://support.gs-group.fr/api";
const hdr = () => ({ Authorization:"Bearer "+localStorage.getItem("nexo_token") });

/* ── Normalisation téléphone → format français XX XX XX XX XX ── */
function formatPhone(raw) {
  const digits = raw.replace(/\D/g,"");
  if(!digits) return raw;
  // Préfixe international → 0X
  const local = digits.startsWith("33") ? "0"+digits.slice(2)
              : digits.startsWith("330") ? "0"+digits.slice(3)
              : digits;
  if(local.length===10 && local.startsWith("0")) {
    return local.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/,"$1 $2 $3 $4 $5");
  }
  return raw;
}

export default function ModalClient({ T, onClose, onCreated, editClient=null }) {
  const isEdit = !!editClient;
  const [form, setForm] = useState({
    nom: editClient?.nom||"", email: editClient?.email||"",
    telephone: editClient?.telephone||"", adresse: editClient?.adresse||"",
    ville: editClient?.ville||"", code_postal: editClient?.code_postal||"",
    siret: editClient?.siret||""
  });
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");
  const [geocoding, setGeocoding] = useState(false);
  const [geoResult, setGeoResult] = useState(
    editClient?.lat ? `📍 ${parseFloat(editClient.lat).toFixed(4)}, ${parseFloat(editClient.lng||0).toFixed(4)}` : ""
  );
  const [doublon,   setDoublon]   = useState(null);
  const [doublonChecking, setDoublonChecking] = useState(false);
  const doublonTimer = useRef(null);
  const [siretStatus, setSiretStatus] = useState(""); // "loading" | "ok" | "error" | ""
  const [siretInfo,   setSiretInfo]   = useState(null);

  const up = (k,v) => { setForm(p=>({...p,[k]:v})); if(k==="email"||k==="nom") triggerDoublonCheck(k,v); };
  const inp = (opts={}) => ({
    background:T.bg, border:`1px solid ${T.border}`,
    borderRadius:9, padding:"10px 13px",
    color:T.text, fontSize:14, outline:"none", width:"100%", ...opts
  });

  /* ── Détection doublon (debounced) ── */
  const triggerDoublonCheck = (key, val) => {
    if(isEdit) return;
    clearTimeout(doublonTimer.current);
    doublonTimer.current = setTimeout(async () => {
      const checkEmail = key==="email" ? val : form.email;
      const checkNom   = key==="nom"   ? val : form.nom;
      if(!checkEmail && !checkNom) { setDoublon(null); return; }
      setDoublonChecking(true);
      try {
        const params = new URLSearchParams();
        if(checkEmail) params.set("email", checkEmail);
        if(checkNom)   params.set("nom",   checkNom);
        const res = await fetch(`${BASE}/clients?${params}`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]);
        const clients = Array.isArray(res)?res:[];
        const match = clients.find(c =>
          (checkEmail && c.email?.toLowerCase()===checkEmail.toLowerCase()) ||
          (checkNom   && c.nom?.toLowerCase()===checkNom.toLowerCase())
        );
        setDoublon(match||null);
      } catch{}
      setDoublonChecking(false);
    }, 600);
  };

  /* ── Validation SIRET ── */
  const validateSiret = async () => {
    const siret = form.siret.replace(/\s/g,"");
    if(siret.length<9) return;
    setSiretStatus("loading"); setSiretInfo(null);
    try {
      const res = await fetch(`https://recherche-entreprises.api.gouv.fr/search?q=${siret}&per_page=1`).then(r=>r.json());
      const ent = res?.results?.[0];
      if(ent) {
        setSiretStatus("ok");
        setSiretInfo({ nom: ent.nom_complet||ent.siege?.sigle||"", siren: ent.siren, ville: ent.siege?.libelle_commune||"" });
        if(!form.nom && ent.nom_complet) setForm(p=>({...p, nom: ent.nom_complet}));
      } else {
        setSiretStatus("error");
      }
    } catch { setSiretStatus("error"); }
  };

  /* ── Géocodage ── */
  const geocode = async () => {
    if(!form.adresse&&!form.ville) return;
    setGeocoding(true);
    try {
      const q = encodeURIComponent(`${form.adresse} ${form.ville} France`);
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`).then(r=>r.json());
      if(res[0]) setGeoResult(`✅ ${parseFloat(res[0].lat).toFixed(4)}, ${parseFloat(res[0].lon).toFixed(4)} — ${res[0].display_name.split(",").slice(0,3).join(",")}`);
      else setGeoResult("❌ Adresse non trouvée");
    } catch{ setGeoResult("❌ Erreur géocodage"); }
    setGeocoding(false);
  };

  /* ── Formatage téléphone au blur ── */
  const onPhoneBlur = () => {
    const formatted = formatPhone(form.telephone);
    if(formatted!==form.telephone) up("telephone", formatted);
  };

  /* ── Soumission ── */
  const handleSubmit = async () => {
    if(!form.nom){ setError("Le nom est obligatoire"); return; }
    if(doublon){ setError(`Doublon détecté : "${doublon.nom}" existe déjà (même nom ou email).`); return; }
    setSaving(true); setError("");
    try {
      let res;
      if(isEdit){
        res = await fetch(`${BASE}/clients/${editClient.id}`,{
          method:"PATCH",
          headers:{...hdr(),"Content-Type":"application/json"},
          body:JSON.stringify(form),
        }).then(r=>r.json());
      } else {
        res = await api.createClient(form);
      }
      if(res.error){ setError(res.error); setSaving(false); return; }
      onCreated&&onCreated(res);
      onClose();
    } catch{ setError("Erreur serveur"); }
    setSaving(false);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(8,12,30,0.22)",backdropFilter:"blur(4px)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:16,padding:30,animation:"nx-modal-open 0.3s cubic-bezier(0.16,1,0.3,1) both",width:520,maxHeight:"92vh",overflowY:"auto"}}>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22}}>
          <div>
            <div style={{fontSize:18,fontWeight:800,color:T.text}}>{isEdit?"✏️ Modifier le client":"👤 Nouveau client"}</div>
            <div style={{fontSize:12,color:T.muted,marginTop:3}}>{isEdit?editClient.nom:"Ajouter un client au fichier"}</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:20}}>✕</button>
        </div>

        {error&&<div style={{background:`${T.danger}15`,border:`1px solid ${T.danger}40`,borderRadius:9,padding:"10px 14px",marginBottom:14,fontSize:13,color:T.danger}}>⚠ {error}</div>}

        {/* Alerte doublon */}
        {doublon&&!error&&(
          <div style={{background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.4)",borderRadius:9,padding:"10px 14px",marginBottom:14,fontSize:13,color:"#f59e0b",display:"flex",gap:8,alignItems:"flex-start"}}>
            <span style={{flexShrink:0}}>⚠️</span>
            <div>
              <div style={{fontWeight:700}}>Doublon détecté</div>
              <div style={{fontSize:12,marginTop:2}}>"{doublon.nom}" existe déjà{doublon.email?" ("+doublon.email+")":""}. Vérifiez avant de créer.</div>
            </div>
          </div>
        )}
        {doublonChecking&&<div style={{fontSize:11,color:T.muted,marginBottom:10}}>🔍 Vérification des doublons…</div>}

        <div style={{display:"flex",flexDirection:"column",gap:13}}>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            <label style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.7px"}}>Nom / Société *</label>
            <input value={form.nom} onChange={e=>up("nom",e.target.value)} placeholder="Ex : Hôtel Lumière" style={inp()}/>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <label style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.7px"}}>Email</label>
              <input type="email" value={form.email} onChange={e=>up("email",e.target.value)} placeholder="contact@exemple.fr" style={inp(doublon?.email===form.email?{borderColor:"#f59e0b"}:{})}/>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <label style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.7px"}}>
                Téléphone
                {form.telephone&&formatPhone(form.telephone)!==form.telephone&&(
                  <span style={{fontSize:10,color:T.accent,fontWeight:500,marginLeft:6}}>→ sera formaté</span>
                )}
              </label>
              <input
                value={form.telephone}
                onChange={e=>up("telephone",e.target.value)}
                onBlur={onPhoneBlur}
                placeholder="03 XX XX XX XX"
                style={inp()}
              />
            </div>
          </div>

          {/* SIRET */}
          <div style={{background:T.bg,borderRadius:10,border:`1px solid ${siretStatus==="ok"?"#10b98155":siretStatus==="error"?"#ef444455":T.border}`,padding:"12px 14px"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:siretInfo?8:0}}>
              <div style={{flex:1}}>
                <label style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.7px",display:"block",marginBottom:6}}>
                  SIRET / SIREN
                  {siretStatus==="loading"&&<span style={{fontWeight:400,marginLeft:6,fontSize:10}}>🔍 Vérification…</span>}
                </label>
                <input value={form.siret} onChange={e=>{up("siret",e.target.value);setSiretStatus("");setSiretInfo(null);}}
                  onBlur={validateSiret}
                  placeholder="Ex : 123 456 789 00012"
                  style={inp({fontFamily:"monospace",letterSpacing:"1px"})}/>
              </div>
              <button onClick={validateSiret} disabled={siretStatus==="loading"||!form.siret}
                style={{marginTop:22,padding:"10px 14px",borderRadius:9,border:`1px solid ${T.accent}`,background:T.accentDim,color:T.accent,fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0,opacity:!form.siret?0.4:1}}>
                {siretStatus==="loading"?"…":"🔎"}
              </button>
            </div>
            {siretStatus==="ok"&&siretInfo&&(
              <div style={{fontSize:12,color:"#10b981",display:"flex",alignItems:"center",gap:6}}>
                <span>✅</span>
                <div><strong>{siretInfo.nom}</strong>{siretInfo.ville&&` · ${siretInfo.ville}`} <span style={{opacity:0.7}}>SIREN {siretInfo.siren}</span></div>
              </div>
            )}
            {siretStatus==="error"&&<div style={{fontSize:12,color:"#ef4444"}}>❌ Entreprise non trouvée — vérifiez le numéro</div>}
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            <label style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.7px"}}>Adresse</label>
            <input value={form.adresse} onChange={e=>up("adresse",e.target.value)} placeholder="12 rue de la Paix" style={inp()}/>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:12}}>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <label style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.7px"}}>Ville</label>
              <input value={form.ville} onChange={e=>up("ville",e.target.value)} placeholder="Mulhouse" style={inp()}/>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <label style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.7px"}}>Code postal</label>
              <input value={form.code_postal} onChange={e=>up("code_postal",e.target.value)} placeholder="68100" style={inp()}/>
            </div>
          </div>

          {/* Géocodage */}
          <div style={{background:T.bg,borderRadius:10,border:`1px solid ${T.border}`,padding:"12px 14px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:geoResult?8:0}}>
              <div style={{fontSize:12,color:T.muted,fontWeight:600}}>📍 Géolocalisation automatique</div>
              <button onClick={geocode} disabled={geocoding||(!form.adresse&&!form.ville)}
                style={{padding:"5px 12px",borderRadius:7,border:`1px solid ${T.accent}`,background:T.accentDim,color:T.accent,fontSize:11,fontWeight:600,cursor:"pointer",opacity:(!form.adresse&&!form.ville)?0.5:1}}>
                {geocoding?"Recherche...":"🔍 Géocoder"}
              </button>
            </div>
            {geoResult&&<div style={{fontSize:11,color:geoResult.startsWith("✅")?T.success:T.danger,marginTop:4}}>{geoResult}</div>}
            {!geoResult&&<div style={{fontSize:11,color:T.muted,marginTop:4}}>Entrez l'adresse puis cliquez sur Géocoder pour positionner le client sur la carte</div>}
          </div>
        </div>

        <div style={{display:"flex",gap:10,marginTop:22}}>
          <button onClick={onClose} style={{flex:1,padding:"11px",borderRadius:10,border:`1px solid ${T.border}`,background:"transparent",color:T.muted,fontSize:14,fontWeight:600,cursor:"pointer"}}>Annuler</button>
          <button onClick={handleSubmit} disabled={saving}
            style={{flex:2,padding:"11px",borderRadius:10,background:`linear-gradient(135deg,${T.accent},${T.purple||"#8b5cf6"})`,border:"none",color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",opacity:saving?0.7:1}}>
            {saving?"Enregistrement...":(isEdit?"✓ Enregistrer":"✓ Créer le client")}
          </button>
        </div>
      </div>
    </div>
  );
}
